/**
 * ReportSheet — Report modal with 4 abuse categories as tappable cards.
 * Opens as a bottom sheet overlay. Selecting a category sends the report
 * and auto-disconnects the current peer.
 */
import { type Component, For } from 'solid-js';
import { IconClose, IconWarning } from './Icons';
import styles from './ReportSheet.module.css';

export type ReportCategory = 'inappropriate' | 'harassment' | 'spam' | 'underage';

export interface ReportSheetProps {
  isOpen: boolean;
  onClose: () => void;
  onReport: (category: ReportCategory) => void;
}

interface CategoryOption {
  id: ReportCategory;
  label: string;
  description: string;
  icon: string;
}

const CATEGORIES: CategoryOption[] = [
  {
    id: 'inappropriate',
    label: 'Inappropriate Content',
    description: 'Nudity, sexual content, or offensive material',
    icon: '\u26D4',
  },
  {
    id: 'harassment',
    label: 'Harassment',
    description: 'Bullying, threats, or hateful behavior',
    icon: '\u{1F6AB}',
  },
  {
    id: 'spam',
    label: 'Spam',
    description: 'Advertising, bots, or repetitive content',
    icon: '\u{1F4E2}',
  },
  {
    id: 'underage',
    label: 'Underage User',
    description: 'Person appears to be under 18 years old',
    icon: '\u{1F6B8}',
  },
];

const ReportSheet: Component<ReportSheetProps> = (props) => {
  const handleCategoryClick = (category: ReportCategory) => {
    props.onReport(category);
  };

  const handleBackdropClick = () => {
    props.onClose();
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      props.onClose();
    }
  };

  return (
    <>
      {/* Backdrop */}
      <div
        class={`${styles.backdrop} ${props.isOpen ? styles.backdropOpen : ''}`}
        onClick={handleBackdropClick}
        onKeyDown={handleKeyDown}
        aria-hidden="true"
      />

      {/* Sheet */}
      <div
        class={`${styles.sheet} ${props.isOpen ? styles.sheetOpen : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label="Report user"
      >
        {/* Drag handle */}
        <div class={styles.dragHandle} aria-hidden="true">
          <div class={styles.dragBar} />
        </div>

        {/* Header */}
        <div class={styles.header}>
          <div class={styles.headerLeft}>
            <IconWarning size={20} />
            <h2 class={styles.title}>Report User</h2>
          </div>
          <button
            class={styles.closeButton}
            onClick={() => props.onClose()}
            aria-label="Close report"
            type="button"
          >
            <IconClose size={18} />
          </button>
        </div>

        <p class={styles.subtitle}>
          Select a reason for reporting. This will end the current chat.
        </p>

        {/* Category cards */}
        <div class={styles.categories} role="list">
          <For each={CATEGORIES}>
            {(cat) => (
              <button
                class={styles.categoryCard}
                onClick={() => handleCategoryClick(cat.id)}
                role="listitem"
                type="button"
                aria-label={`Report for ${cat.label}: ${cat.description}`}
              >
                <span class={styles.categoryIcon} aria-hidden="true">
                  {cat.icon}
                </span>
                <div class={styles.categoryText}>
                  <span class={styles.categoryLabel}>{cat.label}</span>
                  <span class={styles.categoryDesc}>{cat.description}</span>
                </div>
              </button>
            )}
          </For>
        </div>
      </div>
    </>
  );
};

export default ReportSheet;
